/**
 * @deprecated RevenueCat is being phased out in favor of Stripe.
 */
import { Purchases, type CustomerInfo, type Offering } from '@revenuecat/purchases-js';

let configuredForUid: string | null = null;

export interface RevenueCatUserProfile {
  email?: string | null;
  displayName?: string | null;
}

function getApiKey(): string {
  const envKey = import.meta.env.VITE_REVENUECAT_API_KEY;

  if (!envKey) {
    throw new Error('Missing RevenueCat API key. Check your .env file.');
  }

  return envKey;
}

function buildAttributes(profile?: RevenueCatUserProfile): { [key: string]: null | string } {
  const attributes: { [key: string]: null | string } = {};
  if (profile?.email) attributes.$email = profile.email;
  if (profile?.displayName) attributes.$displayName = profile.displayName;
  return attributes;
}

export const revenuecatClient = {
  getOfferingIdentifier(): string | undefined {
    const env = import.meta.env.VITE_REVENUECAT_OFFERING_ID;
    return env && env !== 'current' ? env : undefined;
  },

  async configure(uid: string, profile?: RevenueCatUserProfile): Promise<void> {
    const attributes = buildAttributes(profile);

    if (Purchases.isConfigured()) {
      if (configuredForUid === uid) {
        if (Object.keys(attributes).length > 0) {
          await Purchases.getSharedInstance().setAttributes(attributes);
        }
        return;
      }

      const instance = Purchases.getSharedInstance();

      if (instance.getAppUserId() !== uid) {
        await instance.changeUser(uid);
      }

      if (Object.keys(attributes).length > 0) {
        await instance.setAttributes(attributes);
      }

      configuredForUid = uid;
      return;
    }

    const instance = Purchases.configure({ apiKey: getApiKey(), appUserId: uid });
    if (Object.keys(attributes).length > 0) {
      await instance.setAttributes(attributes);
    }
    configuredForUid = uid;
  },

  async getCustomerInfo(): Promise<CustomerInfo> {
    const instance = Purchases.getSharedInstance();
    return instance.getCustomerInfo();
  },

  async presentPaywall(): Promise<CustomerInfo> {
    const instance = Purchases.getSharedInstance();
    const offerings = await instance.getOfferings();
    const offeringId = this.getOfferingIdentifier();
    const offering: Offering | null =
      (offeringId ? offerings.all[offeringId] : null) ?? offerings.current;

    if (!offering) {
      throw new Error('No RevenueCat paywall/offering is configured.');
    }

    const result = await instance.presentPaywall({
      offering,
      showDiscountCodeField: true,
    });
    return result.customerInfo;
  },
};
